import { useState, useRef, useCallback, useEffect } from 'react'
import {
  Box,
  Flex,
  VStack,
  Text,
  Icon,
  useColorMode,
} from '@chakra-ui/react'
import MDEditor, { commands } from '@uiw/react-md-editor'
import {
  Bold, Italic, Strikethrough, Code, Link2,
  Heading1, Heading2, Heading3,
  List, ListOrdered, ListChecks,
  Quote, Code2, Image, PanelRight, FileText,
} from 'lucide-react'
import { EditorSizeFooter } from '../EditorSizeFooter'
import { ContentRenderer } from '@/components/common/ContentRenderer'
import type { EditorPaneProps } from '../types'
import '@uiw/react-md-editor/markdown-editor.css'

export function MarkdownEditor({
  content,
  onContentChange,
  viewMode,
  readOnly = false,
  storageUsage,
  showRawView = false,
  format,
}: EditorPaneProps) {
  const { colorMode } = useColorMode()
  const [splitPosition, setSplitPosition] = useState(50) // percentage
  const containerRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)

  // Custom toolbar commands with Lucide React icons
  const customCommands = [
    // Text formatting
    {
      ...commands.bold,
      icon: <Bold size={18} />,
    },
    {
      ...commands.italic,
      icon: <Italic size={18} />,
    },
    {
      ...commands.strikethrough,
      icon: <Strikethrough size={18} />,
    },
    // Headings
    commands.divider,
    {
      ...commands.title1,
      icon: <Heading1 size={18} />,
    },
    {
      ...commands.title2,
      icon: <Heading2 size={18} />,
    },
    {
      ...commands.title3,
      icon: <Heading3 size={18} />,
    },
    // Lists
    commands.divider,
    {
      ...commands.unorderedListCommand,
      icon: <List size={18} />,
    },
    {
      ...commands.orderedListCommand,
      icon: <ListOrdered size={18} />,
    },
    {
      ...commands.checkedListCommand,
      icon: <ListChecks size={18} />,
    },
    // Others
    commands.divider,
    {
      ...commands.quote,
      icon: <Quote size={18} />,
    },
    {
      ...commands.code,
      icon: <Code size={18} />,
    },
    {
      ...commands.codeBlock,
      icon: <Code2 size={18} />,
    },
    {
      ...commands.link,
      icon: <Link2 size={18} />,
    },
    {
      ...commands.image,
      icon: <Image size={18} />,
    },
  ]

  // In read-only mode, only show preview
  const showEditor = !readOnly && (viewMode === 'split' || viewMode === 'editor')
  const showPreview = viewMode === 'split' || viewMode === 'preview'
  const showSplit = showEditor && showPreview

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isDragging.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return

    const container = containerRef.current
    const rect = container.getBoundingClientRect()
    const newPosition = ((e.clientX - rect.left) / rect.width) * 100

    // Clamp between 20% and 80%
    setSplitPosition(Math.min(80, Math.max(20, newPosition)))
  }, [])

  const handleMouseUp = useCallback(() => {
    isDragging.current = false
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }, [])

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove, handleMouseUp])


  return (
    <Flex
      ref={containerRef}
      flex={1}
      minH={0}
      direction={{ base: 'column', md: showSplit ? 'row' : 'column' }}
      overflow="hidden"
    >
      {/* Editor pane - only in edit mode */}
      {showEditor && onContentChange && (
        <Flex
          direction="column"
          minW={0}
          minH={0}
          overflow="hidden"
          w={{ base: '100%', md: showSplit ? `${splitPosition}%` : '100%' }}
          flex={{ base: 1, md: showSplit ? 'none' : 1 }}
          data-color-mode={colorMode}
        >
          <Box className="editor-scrollbar" flex={1} minH={0} overflow="hidden">
            <MDEditor
              value={content}
              onChange={(val) => onContentChange(val || '')}
              preview="edit"
              hideToolbar={false}
              visibleDragbar={false}
              height="100%"
              commands={customCommands}
              extraCommands={[]}
              overflow={false}
              style={{
                border: 'none',
                borderRadius: 0,
              }}
            />
          </Box>
          <EditorSizeFooter content={content} usage={storageUsage} />
        </Flex>
      )}

      {/* Resizable divider - only in split mode */}
      {showSplit && (
        <Box
          w="3px"
          minW="3px"
          bg={colorMode === 'dark' ? 'stone.700' : 'stone.200'}
          cursor="col-resize"
          onMouseDown={handleMouseDown}
          position="relative"
          transition="all 0.15s"
          _hover={{
            w: '6px',
            minW: '6px',
            bg: colorMode === 'dark' ? 'stone.600' : 'stone.400',
          }}
          _active={{
            w: '6px',
            minW: '6px',
            bg: 'brand.500',
          }}
          display={{ base: 'none', md: 'block' }}
        />
      )}

      {/* Mobile divider - horizontal */}
      {showSplit && (
        <Box
          h="2px"
          bg={colorMode === 'dark' ? 'stone.700' : 'stone.200'}
          display={{ base: 'block', md: 'none' }}
        />
      )}

      {/* Preview pane - using our own preview, not MDEditor's built-in */}
      {showPreview && (
        <Flex
          flex={1}
          direction="column"
          minW={0}
          minH={0}
          bg={colorMode === 'dark' ? 'stone.800' : 'stone.50'}
          overflow="hidden"
          data-color-mode={colorMode}
        >
          <Box flex={1} overflow="auto" className="editor-scrollbar" p={0}>
            {content.trim() ? (
              <Box
                maxW="4xl"
                mx="auto"
                w="100%"
              >
                {showRawView ? (
                  <Box
                    as="pre"
                    fontFamily="mono"
                    fontSize="sm"
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                    p={4}
                    color={colorMode === 'dark' ? 'stone.200' : 'stone.800'}
                  >
                    {content}
                  </Box>
                ) : (
                  <ContentRenderer source={content} format={format} />
                )}
              </Box>
            ) : (
              <Flex
                flex={1}
                align="center"
                justify="center"
                minH="200px"
              >
                <VStack spacing={3}>
                  <Icon
                    as={readOnly ? FileText : PanelRight}
                    boxSize={10}
                    color={colorMode === 'dark' ? 'stone.600' : 'stone.300'}
                    strokeWidth={1.5}
                  />
                  <Text
                    color={colorMode === 'dark' ? 'stone.500' : 'stone.400'}
                    fontSize="sm"
                    fontWeight="medium"
                  >
                    {readOnly ? 'No content' : 'Start typing to see preview'}
                  </Text>
                </VStack>
              </Flex>
            )}
          </Box>
        </Flex>
      )}
    </Flex>
  )
}
